import React, { useState, useEffect } from 'react';
import {useNavigate} from 'react-router-dom';import {useTranslation} from 'react-i18next';import Header from '@/components/Header';
import {BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area} from 'recharts';import {MessageSquare, BookOpen} from 'lucide-react';import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';


const COLORS = ['#6366f1', '#ec4899', '#22c55e', '#f97316', '#8b5cf6', '#0ea5e9', '#eab308', '#ef4444', '#14b8a6', '#f43f5e'];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

export default function AnalyticsPage({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const analyticsRes = await axios.get(`${API}/analytics/overview`, { withCredentials: true });
        setData(analyticsRes.data);
      } catch (e) {
        console.error('Analytics load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="page-container theme-bg">
        <Header user={user} />
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const chartData = data?.daily_activity || [];
  // Format dates for display
  const formattedChart = chartData.map(d => ({
    ...d,
    label: new Date(d.date + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })
  }));

  return (
    <div className="page-container" style={{ background: 'var(--theme-background)' }}>
      <Header user={user} />

      <main className="page-content max-w-6xl mx-auto p-4 md:p-8 pb-24 space-y-6 w-full">
        <h1 className="text-2xl font-bold theme-text" style={{ fontFamily: 'var(--theme-heading-family)' }}>
          {t('analytics.title', 'Study Analytics')}
        </h1>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={MessageSquare} label={t('analytics.questions', 'Questions Asked')} value={data?.total_questions || 0} color="bg-indigo-500" />
          <StatCard icon={BookOpen} label={t('analytics.conversations', 'Conversations')} value={data?.total_conversations || 0} color="bg-emerald-500" />
        </div>

        {/* Daily Activity Chart */}
        <div className="bg-card rounded-2xl border border-border p-5" data-testid="daily-activity-chart">
          <h2 className="text-lg font-semibold text-foreground mb-4">{t('analytics.daily_activity', 'Daily Activity (Last 30 Days)')}</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedChart}>
                <defs>
                  <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '12px', fontSize: '13px' }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <Area type="monotone" dataKey="messages" stroke="hsl(var(--primary))" fillOpacity={1} fill="url(#colorMessages)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Topics */}
          <div className="bg-card rounded-2xl border border-border p-5" data-testid="top-topics">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t('analytics.top_topics', 'Top Topics')}</h2>
            {(data?.top_topics?.length || 0) === 0 ? (
              <p className="text-muted-foreground text-sm">{t('analytics.no_data', 'Start studying to see your analytics')}</p>
            ) : (
              <div className="space-y-2.5">
                {data.top_topics.map((topic, i) => (
                  <div key={topic.topic} className="flex items-center gap-3">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-foreground flex-1 truncate">{topic.topic}</span>
                    <span className="text-sm font-semibold text-muted-foreground">{topic.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top Books */}
          <div className="bg-card rounded-2xl border border-border p-5" data-testid="top-books">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t('analytics.top_books', 'Most Referenced Books')}</h2>
            {(data?.top_books?.length || 0) === 0 ? (
              <p className="text-muted-foreground text-sm">{t('analytics.no_data', 'Start studying to see your analytics')}</p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.top_books} layout="vertical">
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis type="category" dataKey="book" tick={{ fontSize: 11 }} width={80} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '12px', fontSize: '13px' }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* Weekly Trend */}
        <div className="bg-card rounded-2xl border border-border p-5" data-testid="weekly-trend">
          <h2 className="text-lg font-semibold text-foreground mb-4">{t('analytics.weekly_trend', 'Weekly Trend')}</h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.weekly_trend || []}>
                <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '12px', fontSize: '13px' }}
                />
                <Bar dataKey="messages" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </main>
    </div>
  );
}
